import React, { useEffect, useState, useCallback } from "react";
import {
    Col,
    Row,
    Card,
    CardBody,
    Button,
    InputGroup,
    Input,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Container,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { Link } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import {
    FormatDate,
    formatDate,
    formatDateTime,
    formatDateTimes,
} from "../../../Components/Common/FormatDate";

import { exportToExcel } from "../../../utilis/exportToExcel";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

import useTableSearch from "../../../hooks/useTableSearch";

import KDSService from "../../../services/OrderingServices/KDSService";
import TableService from "../../../services/OrderingServices/TableService";

const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const customStyles = {
    headCells: {
        style: {
            fontSize: "0.92rem",
            fontWeight: 610,
        },
    },
    rows: {
        style: {
            fontSize: "0.9rem",
        },
    },
};

const KitchenReport = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState();
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [dateRange, setDateRange] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Summary state — mapped to new response fields
    const [totalOrdered, setTotalOrdered] = useState(0);
    const [totalPrepared, setTotalPrepared] = useState(0);
    const [totalServed, setTotalServed] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [avgPrepDuration, setAvgPrepDuration] = useState(0);
    const [avgServeDuration, setAvgServeDuration] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const [filters, setFilters] = useState({
        orderRefNumber: "",
        productName: "",
        Status: "",
        FromDate: "",
        ToDate: "",
    });
    const [debouncedFilters, setDebouncedFilters] = useState(filters);
    const [exportLoading, setExportLoading] = useState(false);

    // Helper: convert seconds → "Xh Ym Zs"
    const formatDuration = (seconds) => {
        if (!seconds && seconds !== 0) return "—";
        const s = Math.round(seconds);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m ${sec}s`;
        return `${sec}s`;
    };

    const toggleDetailModal = (row) => {
        setIsDetailOpen(!isDetailOpen);
        setSelectedItem(row);
    };

    // Status badge helper
    const StatusBadge = ({ status }) => {
        const map = {
            Served: { bg: "#d1fae5", color: "#065f46" },
            Prepared: { bg: "#dbeafe", color: "#1e40af" },
            Pending: { bg: "#fef3c7", color: "#92400e" },
        };
        const style = map[status] || { bg: "#f3f4f6", color: "#374151" };
        return (
            <span
                style={{
                    backgroundColor: style.bg,
                    color: style.color,
                    padding: "2px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                }}
            >
                {status}
            </span>
        );
    };

    const columns = [
        {
            name: "S.N",
            cell: (_, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
        },
        {
            name: "Order Ref",
            selector: (row) => row.orderRefNumber,
            sortable: true,
            minWidth: "170px",
        },
        {
            name: "Table",
            selector: (row) => row.tableName,
            sortable: true,
            minWidth: "110px",
        },
        {
            name: "Product",
            selector: (row) => row.productName,
            sortable: true,
            minWidth: "140px",
        },
        {
            name: "Qty",
            selector: (row) => row.quantity,
            sortable: true,
            right: true,
            width: "70px",
        },
        {
            name: "Status",
            cell: (row) => <StatusBadge status={row.orderItemStatus} />,
            sortable: true,
            minWidth: "110px",
            center: true,
        },
        {
            name: "Order Created",
            selector: (row) => formatDate(row.orderCreatedAt),
            sortable: true,
            minWidth: "150px",
        },
        {
            name: "Prepared At",
            selector: (row) => row.preparedAt ? formatDate(row.preparedAt) : "—",
            sortable: true,
            minWidth: "150px",
        },
        {
            name: "Prep Duration",
            cell: (row) => formatDuration(row.preparedDurationSeconds),
            sortable: true,
            right: true,
            minWidth: "130px",
        },
        {
            name: "Served At",
            selector: (row) => row.servedAt ? formatDate(row.servedAt) : "—",
            sortable: true,
            minWidth: "150px",
        },
        {
            name: "Serve Duration",
            cell: (row) => formatDuration(row.servedDurationSeconds),
            sortable: true,
            right: true,
            minWidth: "140px",
        },
        {
            name: "Action",
            width: "80px",
            center: true,
            cell: (row) => (
                <Button
                    className="btn btn-soft-success btn-sm"
                    onClick={() => toggleDetailModal(row)}
                >
                    <i className="bx bx-show" />
                </Button>
            ),
        },
    ];

    // Client-side search across key fields
    const { searchTerm, setSearchTerm, filteredData } = useTableSearch(data, [
        "orderRefNumber",
        "productName",
        "tableName",
        "orderItemStatus",
    ]);

    const fetchData = async (
        page = currentPage,
        size = perPage,
        currentFilters = debouncedFilters,
    ) => {
        try {
            setLoading(true);
            const response = await KDSService.getAllOrder({
                PageNumber: page,
                PageSize: size,
                FromDate: currentFilters.FromDate,
                ToDate: currentFilters.ToDate,
                Status: currentFilters.Status,
            });

            // Map new response structure
            setData(response?.items || []);
            setTotalOrdered(response?.totalOrdered ?? 0);
            setTotalPrepared(response?.totalPrepared ?? 0);
            setTotalServed(response?.totalServed ?? 0);
            setTotalPending(response?.totalPending ?? 0);
            setAvgPrepDuration(response?.avgPrepDurationSeconds ?? 0);
            setAvgServeDuration(response?.avgServeDurationSeconds ?? 0);
            setTotalRows(response?.items?.length ?? 0); // or response.totalCount if paginated server-side
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    // Default last 1 month
    useEffect(() => {
        const today = new Date();
        const lastMonth = new Date();
        lastMonth.setMonth(today.getMonth() - 1);
        setFromDate(lastMonth);
        setToDate(today);
        setDateRange([lastMonth, today]);
        setFilters((prev) => ({
            ...prev,
            FromDate: formatLocalDate(lastMonth),
            ToDate: formatLocalDate(today),
        }));
    }, []);

    // Debounce filters
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedFilters(filters);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(id);
    }, [filters]);

    // Fetch on page/perPage/filter change
    useEffect(() => {
        if (debouncedFilters.FromDate && debouncedFilters.ToDate) {
            fetchData(currentPage, perPage);
        }
    }, [currentPage, perPage, debouncedFilters]);

    const handleDateRangeChange = (dates) => {
        if (dates.length === 2) {
            setDateRange(dates);
            setFromDate(dates[0]);
            setToDate(dates[1]);
            setFilters((prev) => ({
                ...prev,
                FromDate: formatLocalDate(dates[0]),
                ToDate: formatLocalDate(dates[1]),
            }));
        }
    };

    const handleExport = async () => {
        if (!fromDate || !toDate) {
            toast.error("Please select a valid date range");
            return;
        }
        try {
            setExportLoading(true);
            const startDate = formatLocalDate(fromDate);
            const endDate = formatLocalDate(toDate);

            const response = await KDSService.getAllOrder({
                PageNumber: 1,
                PageSize: 10000,
                FromDate: startDate,
                ToDate: endDate,
            });

            const exportData = response?.items || [];
            if (!exportData.length) {
                toast.error("No data found for the selected date range");
                return;
            }

            const columnMapping = {
                "S.N": (row, index) => index + 1,
                "Order Ref": "orderRefNumber",
                "Table": "tableName",
                "Product": "productName",
                "Qty": "quantity",
                "Status": "orderItemStatus",
                "Order Created": (row) => formatDate(row.orderCreatedAt),
                "Prepared At": (row) => row.preparedAt ? formatDate(row.preparedAt) : "",
                "Prep Duration": (row) => formatDuration(row.preparedDurationSeconds),
                "Served At": (row) => row.servedAt ? formatDate(row.servedAt) : "",
                "Serve Duration": (row) => formatDuration(row.servedDurationSeconds),
            };

            exportToExcel(exportData, columnMapping, `KitchenReport_${startDate}_to_${endDate}.xlsx`);
            toast.success("Export successful!");
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Failed to export data");
        } finally {
            setExportLoading(false);
        }
    };

    const SummaryCard = ({ title, value, color, icon }) => (
        <Card className="shadow-sm border-0">
            <CardBody className="d-flex align-items-center justify-content-between">
                <div className="w-100">
                    <h6 className="fw-semibold text-uppercase" style={{ color }}>
                        {title}
                    </h6>
                    <div className="d-flex justify-content-between w-100">
                        <h4 className="fs-2 fw-bold" style={{ color }}>{value}</h4>
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                                backgroundColor: color + "20",
                                color,
                                width: "36px",
                                height: "36px",
                            }}
                        >
                            <i className={`${icon} fs-5`} />
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    // Status filter options
    const statusOptions = [
        { value: "", label: "All Statuses" },
        { value: "Served", label: "Served" },
        { value: "Prepared", label: "Prepared" },
        { value: "Pending", label: "Pending" },
    ];

    return (
        <Container fluid>
            <div className="page-content">
                <BreadCrumb title="Kitchen Report" pageTitle="Reports" />

                {/* ── Summary Cards ── */}
                <Row className="mb-3">
                    <Col lg={2} md={4} sm={6}>
                        <SummaryCard title="Total Ordered" value={totalOrdered}
                            color="#0d6efd" icon="bx bx-receipt" />
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <SummaryCard title="Total Prepared" value={totalPrepared}
                            color="#6f42c1" icon="bx bx-check-circle" />
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <SummaryCard title="Total Served" value={totalServed}
                            color="#28a745" icon="bx bx-food-menu" />
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <SummaryCard title="Pending" value={totalPending}
                            color="#ffc107" icon="bx bx-time-five" />
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <SummaryCard title="Avg Prep Time" value={formatDuration(avgPrepDuration)}
                            color="#17a2b8" icon="bx bx-stopwatch" />
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <SummaryCard title="Avg Serve Time" value={formatDuration(avgServeDuration)}
                            color="#fd7e14" icon="bx bx-run" />
                    </Col>
                </Row>

                <div
                    className="p-3 p-lg-4"
                    style={{
                        background: "#ffffff",
                        borderRadius: "0.5rem",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                        border: "1px solid #eee",
                    }}
                >
                    <Row className="justify-content-between align-items-center mb-3">


                        <Col md={12}>
                            <Row className="justify-content-end align-items-center g-2">


                                <Col md={4}>
                                    <Select
                                        options={statusOptions}
                                        onChange={(opt) =>
                                            setFilters({ ...filters, Status: opt?.value ?? "" })
                                        }
                                        placeholder="Status"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>
                                <Col md={6}>
                                    <InputGroup>
                                        <Flatpickr
                                            options={{ mode: "range", dateFormat: "Y-m-d" }}
                                            value={dateRange}
                                            onChange={handleDateRangeChange}
                                            placeholder="Select Date Range"
                                            className="form-control"
                                        />
                                        <InputGroupText style={{ cursor: "pointer" }}>
                                            <i className="bx bx-calendar" />
                                        </InputGroupText>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <hr style={{ borderColor: "#000", marginTop: "0" }} />

                    {/* Desktop table */}
                    <div className="d-none d-lg-block">
                        <CustomDataTable
                            responsive
                            striped
                            pagination
                            fixedHeader
                            persistTableHead
                            progressPending={loading}
                            customStyles={customStyles}
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            paginationDefaultPage={currentPage}
                            paginationPerPage={perPage}
                            progressComponent={
                                <div className="my-3">
                                    <Triangle visible height="80" width="80"
                                        color="#5B71B9" ariaLabel="triangle-loading" />
                                    <h5 className="mt-1">Loading...</h5>
                                </div>
                            }
                            columns={columns}
                            data={filteredData}
                            noDataComponent={
                                <div className="text-center my-5 w-100">
                                    <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                                    <h5 className="fw-semibold text-secondary">No data available</h5>
                                </div>
                            }
                        />
                    </div>
                </div>

                {/* ── Mobile cards ── */}
                {loading ? (
                    <div className="my-5 d-flex justify-content-center d-md-none">
                        <Triangle visible height="60" width="60" color="#5B71B9" ariaLabel="triangle-loading" />
                    </div>
                ) : (
                    filteredData?.map((item, index) => (
                        <Card key={item.orderDetailId || index}
                            className="mb-3 shadow-sm d-block d-lg-none mt-2">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 text-primary">{item.orderRefNumber}</h6>
                                    <StatusBadge status={item.orderItemStatus} />
                                </div>
                                <h6 className="mb-2 text-muted" style={{ fontSize: "13px" }}>
                                    {item.tableName}
                                </h6>
                                <h6 className="mb-2 d-flex justify-content-between">
                                    <strong>Product</strong> {item.productName}
                                </h6>
                                <h6 className="mb-2 d-flex justify-content-between">
                                    <strong>Qty</strong> {item.quantity}
                                </h6>
                                <h6 className="mb-2 d-flex justify-content-between">
                                    <strong>Prep Duration</strong> {formatDuration(item.preparedDurationSeconds)}
                                </h6>
                                <h6 className="mb-0 d-flex justify-content-between">
                                    <strong>Serve Duration</strong> {formatDuration(item.servedDurationSeconds)}
                                </h6>
                            </CardBody>
                        </Card>
                    ))
                )}
            </div>
        </Container>
    );
};

export default KitchenReport;
